/**
 * Live TV cart source sync — keeps _itemSource honest for two stale-basket cases:
 *
 * 1. TV → MA: a COA card stamps properties[_itemSource]=TV at add-to-cart
 *    (live-tv-auction-product-form.liquid). If that lot ends while the item still sits in the
 *    basket, the stamp is never revisited, so an order placed after the lot came off air is
 *    still tagged TV.
 * 2. MA → FPC: a missed-auction card stamps _itemSource=MA plus _auctionCode (app.js /
 *    missed-auction-card.liquid). Missed Auctions is a rolling last-24-hours list — if the
 *    shopper sits on the item for days before checking out, the auction has long since dropped
 *    off that list, but the basket line still claims to be a missed-auction purchase, still
 *    carries the old _auctionCode, and (for a Buy All add) still carries the bundle properties
 *    the Buy All discount function keys off. Once that happens the line should look like a
 *    normal full-price item: _itemSource=FPC, every MA/Buy-All-only property removed.
 *
 * Both cases share the same constraint: line item properties are frozen the moment checkout
 * starts, so the basket is the last place either can be corrected — there is no Admin API
 * mutation that rewrites them afterwards.
 *
 * Runs from the app embed, so it is present on every page: one pass on load, another whenever the
 * tab becomes visible again, and a poll while the shopper sits on the cart page waiting to check
 * out. Once they are inside Shopify checkout nothing here can reach the line any more.
 *
 * Safety rule for both flips: only act when the check *proves* the line is stale. Any uncertainty
 * — endpoint unreachable, malformed payload, an unexpectedly empty result — leaves the cart
 * untouched. A missed flip is a reporting mismatch or a stale discount; a wrong flip sells a live
 * or still-valid lot as something it is not.
 *
 * Set localStorage ltv_cart_source_debug=1 for a console trace.
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-live-tv-cart-source-sync]';
  var TV_SOURCE = 'TV';
  var MA_SOURCE = 'MA';
  var FPC_SOURCE = 'FPC';
  var ITEM_SOURCE_KEY = '_itemSource';
  var AUCTION_CODE_KEY = '_auctionCode';
  /** Removed outright (not just overwritten) when a line flips MA → FPC: it should look like a
   *  plain full-price add, and stale Buy All properties are what would let the checkout discount
   *  function keep granting a bundle price after the bundle itself is no longer offered. */
  var MA_ONLY_PROPERTY_KEYS = [
    AUCTION_CODE_KEY,
    '_isBuyAll',
    '_discountCode',
    '_buyAllPrice',
    '_buyAllDiscount',
    '_buyAllApplicableProductCodes',
    '_pnp',
  ];
  var DEFAULT_COA_API_URL = '/apps/live-tv/currently-on-air';
  var DEFAULT_LAST24_API_URL = '/apps/live-tv/last-24-hours';
  /* Matches the COA updater's cadence — the shopper's cart page should not learn of an auction end
     later than the Live TV page would. */
  var POLL_INTERVAL_MS = 10000;
  /* Gap between the two reads that have to agree before a "nothing found" result counts as real.
     Applies to both: COA's "nothing on air" (its auth-fallback branch answers the same way as a
     real end) and last-24-hours coming back with zero codes at all (which would otherwise read as
     "every missed-auction line in every basket is stale" — a blip must not cause that). */
  var EMPTY_CONFIRM_DELAY_MS = 8000;
  /* One pass rewrites at most this many lines per source; a cart cannot legitimately hold more
     stale lines than this, so the cap only ever stops a loop that has stopped making progress. */
  var MAX_FLIPS_PER_PASS = 20;
  var MAX_SNAPSHOT_DEPTH = 8;
  var DEBUG_KEY = 'ltv_cart_source_debug';

  var coaApiUrl = DEFAULT_COA_API_URL;
  var last24ApiUrl = DEFAULT_LAST24_API_URL;
  var passRunning = false;
  var pollTimer = null;
  /* Cart line keys whose rewrite was rejected. A successful change mints a new key, so a key can
     only still be here because it failed — without this the poll would re-attempt the same rejected
     line every tick for as long as the shopper stays on the page. Cleared by the next page load. */
  var failedLineKeys = {};

  function isDebugEnabled() {
    try {
      return typeof window !== 'undefined' && window.localStorage && window.localStorage.getItem(DEBUG_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function debugLog(label, detail) {
    if (!isDebugEnabled()) return;
    if (detail !== undefined) console.log('[LTV][CartSource] ' + label, detail);
    else console.log('[LTV][CartSource] ' + label);
  }

  /** Auction codes arrive from AMS, Kafka and Liquid — compare them case/whitespace-insensitively. */
  function normCode(v) {
    return v == null ? '' : String(v).trim().toLowerCase();
  }

  function delay(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  function isCartPage() {
    var path = String(window.location.pathname || '').replace(/\/+$/, '');
    return /(^|\/)cart$/.test(path);
  }

  // ===========================================================================
  // CURRENTLY ON AIR (feeds the TV → MA flip)
  // ===========================================================================

  /**
   * Every auction code anywhere in the COA payload, keyed by name rather than by a fixed path:
   * the main lot carries auctionCode/packageCode, group and MLA rows carry auctionCode plus the
   * mlaAuctionCode / primeAuctionCode aliases, and AMS varies the casing and nests mlauctions rows
   * inside group rows. Walking by key name means an unfamiliar shape yields extra codes to match
   * against, never a missing one — the failure direction that leaves a live line alone.
   */
  function collectAuctionCodesByKeyName(node, codes, depth) {
    if (!node || typeof node !== 'object' || depth > MAX_SNAPSHOT_DEPTH) return codes;
    if (Array.isArray(node)) {
      for (var i = 0; i < node.length; i++) collectAuctionCodesByKeyName(node[i], codes, depth + 1);
      return codes;
    }
    var keys = Object.keys(node);
    for (var k = 0; k < keys.length; k++) {
      var key = keys[k];
      var value = node[key];
      var lower = key.toLowerCase();
      if (value != null && typeof value === 'object') {
        collectAuctionCodesByKeyName(value, codes, depth + 1);
      } else if (lower === 'packagecode' || lower.indexOf('auctioncode') !== -1) {
        var code = normCode(value);
        if (code) codes[code] = true;
      }
    }
    return codes;
  }

  /**
   * @returns {Promise<{known: boolean, live: boolean, codes: Object}>} `known:false` means the
   * endpoint could not be read — the caller must treat that as "no information", not as an end.
   */
  function readCurrentlyOnAir() {
    return fetch(coaApiUrl, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(function (res) {
        if (!res.ok) throw new Error('currently-on-air responded ' + res.status);
        return res.json();
      })
      .then(function (json) {
        if (!json || json.success !== true) return { known: false, live: false, codes: {} };
        var live = json.isLive === true && !!json.data;
        return {
          known: true,
          live: live,
          codes: live ? collectAuctionCodesByKeyName(json.data, {}, 0) : {},
        };
      })
      .catch(function (error) {
        debugLog('currently-on-air read failed', error && error.message);
        return { known: false, live: false, codes: {} };
      });
  }

  // ===========================================================================
  // LAST 24 HOURS / MISSED AUCTIONS (feeds the MA → FPC flip)
  // ===========================================================================

  /**
   * Every auction code still offered on the Missed Auctions page right now: the top-level auction
   * plus its nested groupauctions/mlauctions codes, but only when the top-level entry is not
   * `visible: false` (no Shopify match, or fully out of stock — see auction-availability.ts). The
   * top-level flag already rolls up availability across every nested alternate, so one flag per
   * auction is enough to gate all of its codes. An auction whose hour has simply aged out of the
   * last-24-hours window will not appear in the payload at all, which the "not found" check below
   * already covers without needing a per-item timestamp.
   *
   * @returns {Promise<{known: boolean, codes: Object}>} `known:false` means the endpoint could not
   * be read (not configured, request failed, malformed payload) — treat as "no information".
   */
  function readCurrentMissedAuctionCodes() {
    return fetch(last24ApiUrl, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(function (res) {
        if (!res.ok) throw new Error('last-24-hours responded ' + res.status);
        return res.json();
      })
      .then(function (json) {
        if (!json || json.success !== true) return { known: false, codes: {} };
        var hours = (json.data && json.data.hours) || [];
        var codes = {};
        for (var h = 0; h < hours.length; h++) {
          var auctions = hours[h].auctions || [];
          for (var a = 0; a < auctions.length; a++) {
            var auction = auctions[a];
            if (auction && auction.visible === false) continue;
            addAuctionCodeAndChildren(auction, codes);
          }
        }
        return { known: true, codes: codes };
      })
      .catch(function (error) {
        debugLog('last-24-hours read failed', error && error.message);
        return { known: false, codes: {} };
      });
  }

  /** One visible auction's own code plus every nested group/MLA code beneath it. */
  function addAuctionCodeAndChildren(auction, codes) {
    if (!auction) return;
    var code = normCode(auction.auctionCode);
    if (code) codes[code] = true;
    var groups = auction.groupauctions || auction.groupAuctions || [];
    for (var g = 0; g < groups.length; g++) {
      var ga = groups[g];
      var gaCode = normCode(ga.auctionCode);
      if (gaCode) codes[gaCode] = true;
      var mlas = ga.mlauctions || ga.mlaAuctions || [];
      for (var m = 0; m < mlas.length; m++) {
        var mlaCode = normCode(mlas[m].auctionCode);
        if (mlaCode) codes[mlaCode] = true;
      }
    }
  }

  // ===========================================================================
  // CART
  // ===========================================================================

  function fetchCart() {
    return fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then(function (res) {
      if (!res.ok) throw new Error('cart.js responded ' + res.status);
      return res.json();
    });
  }

  function linesWithSource(cart, source, requireAuctionCode) {
    var items = (cart && cart.items) || [];
    var out = [];
    for (var i = 0; i < items.length; i++) {
      var props = items[i].properties || {};
      if (String(props[ITEM_SOURCE_KEY] || '').trim().toUpperCase() !== source) continue;
      if (requireAuctionCode && !normCode(props[AUCTION_CODE_KEY])) continue;
      out.push({ line: i + 1, item: items[i] });
    }
    return out;
  }

  /** Live TV lines that can be verified: source TV and an auction code to check it against. */
  function tvLines(cart) {
    return linesWithSource(cart, TV_SOURCE, true);
  }

  /** Missed-auction lines that can be verified: source MA and an auction code to check it against. */
  function maLines(cart) {
    return linesWithSource(cart, MA_SOURCE, true);
  }

  function firstEndedTvLine(cart, coa) {
    var entries = tvLines(cart);
    for (var i = 0; i < entries.length; i++) {
      if (failedLineKeys[entries[i].item.key]) continue;
      var code = normCode(entries[i].item.properties[AUCTION_CODE_KEY]);
      if (!coa.codes[code]) return entries[i];
    }
    return null;
  }

  function firstStaleMaLine(cart, currentMa) {
    var entries = maLines(cart);
    for (var i = 0; i < entries.length; i++) {
      if (failedLineKeys[entries[i].item.key]) continue;
      var code = normCode(entries[i].item.properties[AUCTION_CODE_KEY]);
      if (!currentMa.codes[code]) return entries[i];
    }
    return null;
  }

  /** Cart Ajax wants the numeric id; /cart.js hands back a number, the theme may hand back a gid. */
  function normalizeSellingPlan(planValue) {
    if (planValue == null) return planValue;
    var s = String(planValue);
    var m = s.match(/SellingPlan\/(\d+)/i);
    if (m) return Number(m[1]);
    var n = parseInt(s, 10);
    return isNaN(n) ? s : n;
  }

  /**
   * /cart/change.js replaces the line's whole property set, so it must be resent in full with only
   * the intended keys changed. selling_plan is resent for the same reason: Budget Pay lines must
   * keep their plan through the change. Rewriting properties changes the line key, and Shopify
   * merges the result into an existing identical line when there is one.
   */
  function changeLineProperties(target, mutateProperties) {
    var item = target.item;
    var source = item.properties || {};
    var properties = {};
    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) properties[key] = source[key];
    }
    mutateProperties(properties);

    var body = {
      line: target.line,
      quantity: item.quantity,
      properties: properties,
    };
    var plan =
      item.selling_plan_allocation &&
      item.selling_plan_allocation.selling_plan &&
      item.selling_plan_allocation.selling_plan.id;
    if (plan != null && plan !== '') body.selling_plan = normalizeSellingPlan(plan);

    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    }).then(function (res) {
      return res
        .json()
        .catch(function () {
          return {};
        })
        .then(function (data) {
          if (!res.ok) {
            throw new Error(data.description || data.message || 'cart/change.js responded ' + res.status);
          }
          return data;
        });
    });
  }

  function flipLineToMissedAuction(target) {
    return changeLineProperties(target, function (properties) {
      properties[ITEM_SOURCE_KEY] = MA_SOURCE;
    });
  }

  /** MA → FPC: the line becomes a plain full-price add, so every MA/Buy-All-only property is
   *  dropped outright rather than just having _itemSource overwritten. */
  function flipLineToFullPriceCart(target) {
    return changeLineProperties(target, function (properties) {
      properties[ITEM_SOURCE_KEY] = FPC_SOURCE;
      for (var i = 0; i < MA_ONLY_PROPERTY_KEYS.length; i++) {
        delete properties[MA_ONLY_PROPERTY_KEYS[i]];
      }
    });
  }

  /**
   * Flips one line per iteration, re-reading the cart in between: a successful change renumbers the
   * lines (and may merge two of them), so indexes from an earlier read cannot be reused. A rejected
   * line is set aside and the pass moves on to the next one, which is also what makes the recursion
   * terminate — every iteration either rewrites a line or removes it from the candidates.
   */
  function flipLines(findTarget, applyFlip, logLabel, referenceData, progress) {
    if (progress.attempts >= MAX_FLIPS_PER_PASS) {
      debugLog('attempt cap reached, stopping pass', progress);
      return Promise.resolve(progress.flipped);
    }
    return fetchCart().then(function (cart) {
      var target = findTarget(cart, referenceData);
      if (!target) return progress.flipped;
      var code = target.item.properties[AUCTION_CODE_KEY];
      progress.attempts++;
      return applyFlip(target).then(
        function () {
          progress.flipped++;
          debugLog(logLabel, { line: target.line, sku: target.item.sku, auctionCode: code });
          return flipLines(findTarget, applyFlip, logLabel, referenceData, progress);
        },
        function (error) {
          failedLineKeys[target.item.key] = true;
          console.error('[LTV][CartSource] Could not rewrite line', {
            label: logLabel,
            line: target.line,
            sku: target.item.sku,
            auctionCode: code,
            error: error && error.message,
          });
          return flipLines(findTarget, applyFlip, logLabel, referenceData, progress);
        },
      );
    });
  }

  function flipEndedTvLines(coa) {
    return flipLines(firstEndedTvLine, flipLineToMissedAuction, 'flipped line TV -> MA', coa, {
      flipped: 0,
      attempts: 0,
    });
  }

  function flipStaleMaLines(currentMa) {
    return flipLines(firstStaleMaLine, flipLineToFullPriceCart, 'flipped line MA -> FPC', currentMa, {
      flipped: 0,
      attempts: 0,
    });
  }

  /**
   * Nothing visible changes — same variant, price and quantity, and _-prefixed properties are never
   * rendered — but the line keys did change, so themes that key their cart UI off them are told to
   * re-render. cart:refresh carries no payload by contract, so a theme that ignores it is unaffected.
   */
  function notifyThemeOfCartChange() {
    try {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (e) {
      debugLog('cart:refresh dispatch failed', e && e.message);
    }
  }

  // ===========================================================================
  // PASS
  // ===========================================================================

  /**
   * Resolves the TV → MA half: no TV lines, no work. Otherwise reads COA; "live" flips ended lines
   * straight away, "nothing on air" is re-confirmed once before anything is rewritten, because the
   * endpoint's auth-fallback branch answers exactly the same way when Shopify auth is momentarily
   * unavailable.
   */
  function runTvPass(tvCount) {
    if (tvCount === 0) return Promise.resolve(0);
    return readCurrentlyOnAir().then(function (coa) {
      if (!coa.known) return 0;
      if (coa.live) return flipEndedTvLines(coa);
      return delay(EMPTY_CONFIRM_DELAY_MS)
        .then(readCurrentlyOnAir)
        .then(function (confirmation) {
          if (!confirmation.known) return 0;
          return flipEndedTvLines(confirmation);
        });
    });
  }

  /**
   * Resolves the MA → FPC half: no MA lines, no work. A last-24-hours read with at least one code
   * is trusted immediately. A read with zero codes at all is unusual enough — it would otherwise
   * mean "every missed-auction line in every basket is stale at once" — that it gets the same
   * re-confirm treatment as COA's "nothing on air", so one blip cannot mass-flip baskets.
   */
  function runMaPass(maCount) {
    if (maCount === 0) return Promise.resolve(0);
    return readCurrentMissedAuctionCodes().then(function (currentMa) {
      if (!currentMa.known) return 0;
      if (Object.keys(currentMa.codes).length > 0) return flipStaleMaLines(currentMa);
      return delay(EMPTY_CONFIRM_DELAY_MS)
        .then(readCurrentMissedAuctionCodes)
        .then(function (confirmation) {
          if (!confirmation.known) return 0;
          return flipStaleMaLines(confirmation);
        });
    });
  }

  function runPass(trigger) {
    if (passRunning) {
      debugLog('pass already running, skipping', trigger);
      return Promise.resolve(0);
    }
    passRunning = true;
    debugLog('pass start', trigger);
    /* -1 until a cart is actually read: a failed read must not be mistaken for an empty cart and
       switch polling off for the rest of the visit. */
    var tvCount = -1;
    var maCount = -1;

    return fetchCart()
      .then(function (cart) {
        tvCount = tvLines(cart).length;
        maCount = maLines(cart).length;
        // TV pass first: flipping TV -> MA can renumber/merge lines, so the MA pass re-reads the
        // cart fresh afterwards rather than reusing indexes from this snapshot.
        return runTvPass(tvCount).then(function (tvFlipped) {
          return runMaPass(maCount).then(function (maFlipped) {
            return tvFlipped + maFlipped;
          });
        });
      })
      .then(function (flipped) {
        if (flipped > 0) notifyThemeOfCartChange();
        return flipped;
      })
      .catch(function (error) {
        console.error('[LTV][CartSource] Cart source sync failed', error && error.message);
        return 0;
      })
      .then(function (flipped) {
        passRunning = false;
        debugLog('pass end', { trigger: trigger, tvLines: tvCount, maLines: maCount, flipped: flipped });
        syncPollingToCart(tvCount, maCount);
        return flipped;
      });
  }

  // ===========================================================================
  // INIT
  // ===========================================================================

  function startPolling() {
    if (pollTimer !== null) return;
    debugLog('polling started');
    pollTimer = setInterval(function () {
      if (document.hidden) return;
      runPass('poll');
    }, POLL_INTERVAL_MS);
  }

  function stopPolling() {
    if (pollTimer === null) return;
    clearInterval(pollTimer);
    pollTimer = null;
    debugLog('polling stopped');
  }

  /**
   * Polling is confined to the cart page holding a Live TV or missed-auction line: that is the
   * shopper waiting with a stamped line in front of them. Every other page, and every cart without
   * one, is covered by the single pass on load — which is what keeps a storewide app embed off a
   * 10s store-wide poll.
   */
  function syncPollingToCart(tvCount, maCount) {
    if (tvCount < 0 || maCount < 0) return;
    if (isCartPage() && (tvCount > 0 || maCount > 0)) startPolling();
    else stopPolling();
  }

  function init() {
    var root = document.querySelector(ROOT_SELECTOR);
    if (!root) return;
    coaApiUrl = root.getAttribute('data-currently-on-air-api-url') || DEFAULT_COA_API_URL;
    last24ApiUrl = root.getAttribute('data-last-24-hours-api-url') || DEFAULT_LAST24_API_URL;

    runPass('load');

    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) runPass('visible');
    });
  }

  if (window.Shopify && window.Shopify.designMode) return;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
